import React, { forwardRef } from "react";
import { cn } from "../../utils/cn";

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "elevated" | "outline" | "glass" | "gradient";
  size?: "sm" | "md" | "lg";
  hover?: boolean;
  clickable?: boolean;
  loading?: boolean;
  glow?: boolean;
  gradient?: "purple" | "blue" | "green" | "orange" | "pink";
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant = "default",
      size = "md",
      hover = false,
      clickable = false,
      loading = false,
      glow = false,
      gradient,
      children,
      ...props
    },
    ref,
  ) => {
    const baseClasses = [
      "virtus-card",
      "relative rounded-xl transition-all duration-300",
      "border border-purple-300",
      clickable && "cursor-pointer",
      loading && "overflow-hidden",
    ]
      .filter(Boolean)
      .join(" ");

    const sizeClasses = {
      sm: "p-3 sm:p-4 md:p-4.5 lg:p-5",
      md: "p-4 sm:p-4.5 md:p-5 lg:p-6 xl:p-7",
      lg: "p-5 sm:p-6 md:p-7 lg:p-8 xl:p-10",
    };

    const variantClasses = {
      default: "bg-white shadow-xs hover:shadow-md",
      elevated: "bg-white shadow-lg hover:shadow-xl",
      outline:
        "bg-white border-2 border-purple-300 hover:border-purple-400 hover:shadow-md",
      glass:
        "bg-white/80 backdrop-blur-xs border-purple-300 shadow-lg hover:shadow-xl",
      gradient: "text-white border-0",
    };

    const gradientClasses = gradient
      ? {
          purple: "bg-linear-to-br from-purple-600 via-purple-700 to-blue-800",
          blue: "bg-linear-to-br from-blue-600 via-blue-700 to-cyan-800",
          green: "bg-linear-to-br from-green-600 via-green-700 to-emerald-800",
          orange: "bg-linear-to-br from-orange-600 via-red-600 to-pink-700",
          pink: "bg-linear-to-br from-pink-600 via-purple-600 to-indigo-800",
        }[gradient]
      : "";

    const hoverClasses =
      hover || clickable ? "hover:scale-[1.02] hover:scale-[1.02]" : "";
    const glowClasses = glow
      ? "hover:shadow-2xl hover:shadow-purple-500/25"
      : "";

    return (
      <div
        ref={ref}
        className={cn(
          baseClasses,
          sizeClasses[size],
          variantClasses[variant],
          gradientClasses,
          hoverClasses,
          glowClasses,
          className,
        )}
        {...props}
      >
        {/* Gradient Border Effect */}
        {variant === "gradient" && (
          <div className="absolute inset-0 rounded-xl bg-linear-to-r from-transparent via-white/10 to-transparent opacity-50" />
        )}

        {/* Loading Shimmer */}
        {loading && (
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-linear-to-r from-transparent via-white/20 to-transparent" />
        )}

        {/* Hover Effect Light */}
        {(hover || clickable) && (
          <div className="absolute inset-0 rounded-xl bg-linear-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        )}

        {/* Content */}
        <div className="relative z-10">{children}</div>
      </div>
    );
  },
);

Card.displayName = "Card";

// Card Header Component
interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  icon,
  action,
  className = "",
  children,
  ...props
}) => {
  return (
    <div
      className={cn(
        "flex items-start justify-between mb-4 sm:mb-5 md:mb-6",
        className,
      )}
      {...props}
    >
      <div className="flex items-start space-x-3 sm:space-x-3.5 md:space-x-4 flex-1">
        {icon && (
          <div className="shrink-0 mt-0.5 sm:mt-0.5 md:mt-1">{icon}</div>
        )}
        <div className="flex-1 min-w-0">
          {title && (
            <h3 className="text-base sm:text-base md:text-lg font-semibold text-gray-900 leading-tight">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="text-sm sm:text-sm md:text-base text-gray-600 mt-1.5 sm:mt-1.5 md:mt-2 leading-relaxed">
              {subtitle}
            </p>
          )}
          {children}
        </div>
      </div>
      {action && (
        <div className="shrink-0 ml-3 sm:ml-3.5 md:ml-4">{action}</div>
      )}
    </div>
  );
};

// Card Content Component
interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {}

export const CardContent: React.FC<CardContentProps> = ({
  className = "",
  children,
  ...props
}) => {
  return (
    <div
      className={cn(
        "text-gray-700 space-y-3 sm:space-y-3.5 md:space-y-4",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

// Card Footer Component
interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  color?: "blue" | "green" | "purple" | "orange" | "red" | "yellow" | "indigo";
  icon?: React.ReactNode;
}

export const CardFooter: React.FC<CardFooterProps> = ({
  className = "",
  children,
  color = "blue",
  icon,
  ...props
}) => {
  const colorClasses = {
    blue: {
      bg: "bg-blue-100",
      text: "text-blue-600",
      border: "border-blue-200",
    },
    green: {
      bg: "bg-green-100",
      text: "text-green-600",
      border: "border-green-200",
    },
    purple: {
      bg: "bg-purple-100",
      text: "text-purple-600",
      border: "border-purple-200",
    },
    orange: {
      bg: "bg-orange-100",
      text: "text-orange-600",
      border: "border-orange-200",
    },
    red: { bg: "bg-red-100", text: "text-red-600", border: "border-red-200" },
    yellow: {
      bg: "bg-yellow-100",
      text: "text-yellow-600",
      border: "border-yellow-200",
    },
    indigo: {
      bg: "bg-indigo-100",
      text: "text-indigo-600",
      border: "border-purple-200",
    },
  };

  const colors = colorClasses[color];

  return (
    <div
      className={cn(
        "flex items-center justify-between mt-4 sm:mt-5 md:mt-6 pt-3.5 sm:pt-4 md:pt-5 border-t",
        colors.border,
        className,
      )}
      {...props}
    >
      <div className="flex items-center space-x-2 sm:space-x-2.5 md:space-x-3">
        {icon && (
          <div className={cn("p-1.5 sm:p-1.5 md:p-2 rounded-md", colors.bg)}>
            {icon}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

// Card Title Component
interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {}

export const CardTitle: React.FC<CardTitleProps> = ({
  className = "",
  children,
  ...props
}) => {
  return (
    <h3
      className={cn(
        "text-base sm:text-lg font-semibold leading-tight tracking-tight",
        className,
      )}
      {...props}
    >
      {children}
    </h3>
  );
};

// Card Description Component
interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

export const CardDescription: React.FC<CardDescriptionProps> = ({
  className = "",
  children,
  ...props
}) => {
  return (
    <p
      className={cn(
        "text-sm sm:text-base text-gray-500 mt-1.5 sm:mt-2 leading-relaxed",
        className,
      )}
      {...props}
    >
      {children}
    </p>
  );
};

export default Card;
export { Card };
